import { ComponentType, MouseEventHandler } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { PosAnimatingProgressBar } from 'src/core/POS/PosProgressBar/PosAnimatingProgressBar';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { PosIconProps } from 'src/svgs/SvgWrapper';
import { ContentId } from 'src/utils/constants/contentId';
import { formatCurrency } from 'src/utils/numberFormatter';
import { Event, SponsoredListingsCampaign } from 'src/WebApiController';

import { BoostSolidIconDisplay } from '../BoostIconDisplay';
import * as styles from './BoostListingTabButton.css';

export type BoostListingTabButtonProps = {
  campaign: SponsoredListingsCampaign;
  event: Event;
  isActive: boolean;
  zoneIcon: ComponentType<PosIconProps>;
  zoneTooltip?: string;
  onClick: MouseEventHandler;
};

/**
 * A tab button for a single campaign in the CampaignTabBar, styled after BoostListingButton.
 * Uses the shared Button component with outlineBrand variant and rect shape.
 */
export const BoostListingTabButton = ({
  campaign,
  isActive,
  zoneIcon: ZoneIcon,
  zoneTooltip,
  onClick,
}: BoostListingTabButtonProps) => {
  const budget = campaign.activeFlight?.flightBudget ?? 0;
  const spend = campaign.activeFlight?.flightSpend ?? 0;
  const remaining = budget - spend;
  const remainingPct = budget > 0 ? (remaining / budget) * 100 : 0;
  const remainingText = formatCurrency(
    remaining,
    campaign.currencyCode ?? 'USD'
  );
  const budgetText = formatCurrency(budget, campaign.currencyCode ?? 'USD');
  const tooltipText = `${remainingText} of ${budgetText} remaining budget`;

  return (
    <Button
      variant="outlineBrand"
      shape="rect"
      className={styles.campaignTabButton}
      color={vars.color.textBrandV2}
      onClick={onClick}
      title={tooltipText}
    >
      <Stack gap="s" alignItems="center">
        <ZoneIcon size="24px" title={zoneTooltip} />
        <Stack gap="s" alignItems="center" title={tooltipText}>
          <PosAnimatingProgressBar
            style={{
              width: '60px',
              backgroundColor: vars.color.surfacePrimary,
            }}
            enableGlow
            isError={!isActive}
            value={remainingPct}
          />
          <span
            style={{
              color: !isActive
                ? vars.color.textErrorV2
                : vars.color.textBrandV2,
            }}
          >
            {remainingText}
          </span>
        </Stack>
      </Stack>
    </Button>
  );
};

export type StartCampaignTabButtonProps = {
  onClick: MouseEventHandler;
  hasCampaigns: boolean;
};

/**
 * A dashed-border tab button for starting a new campaign, styled after BoostListingButton.
 * Shows "Boost Event" when no campaigns exist, "Add Campaign" when campaigns are present.
 */
export const StartCampaignTabButton = ({
  onClick,
  hasCampaigns,
}: StartCampaignTabButtonProps) => {
  return (
    <Button
      variant="outlineBrand"
      shape="rect"
      className={styles.campaignTabButton}
      color={vars.color.textBrandV2}
      onClick={onClick}
    >
      <Stack gap="s" alignItems="center">
        <BoostSolidIconDisplay size={vars.iconSize.s} />
        {hasCampaigns ? 'Add Campaign' : <Content id={ContentId.BoostEvent} />}
      </Stack>
    </Button>
  );
};
